/* Cliente REST do admin-push de notificações (ADR-0687). Staff lista/compõe/publica/remove via
   `/v1/admin/notifications` (cookie + RBAC no admin-service). A listagem é paginada por cursor
   keyset (`{ items, nextCursor }`) com filtros server-side (a página é o único recorte que chega). */
import {
  type BulkDeleteDraftNotificationsResponse,
  bulkDeleteDraftNotificationsResponseDto,
  type CreateUserNotificationBody,
  type UserNotification,
  type UserNotificationAudience,
  type UserNotificationExpiryFilter,
  type UserNotificationStatus,
  userNotificationPageDto,
} from '@planuze/admin-contracts';
import { ApiError, buildQuery, parseJson, readErrorCode, request } from './http.js';

async function notifError(res: Response): Promise<ApiError> {
  const code = await readErrorCode(res);
  return new ApiError(res.status, 'server_error', code || 'request failed');
}

export interface AdminNotificationsQuery {
  readonly search?: string | undefined;
  readonly status?: UserNotificationStatus | undefined;
  /** Multi-seleção de severidade; enviada como CSV (`info,warning`) na query. */
  readonly severities?: readonly string[] | undefined;
  readonly audience?: UserNotificationAudience | undefined;
  readonly expiry?: UserNotificationExpiryFilter | undefined;
  readonly cursor?: string | undefined;
}

/** GET /v1/admin/notifications — página keyset com filtros server-side. */
export async function listAdminNotifications(
  query: AdminNotificationsQuery,
): Promise<{ readonly items: readonly UserNotification[]; readonly nextCursor: string | null }> {
  const severity =
    query.severities !== undefined && query.severities.length > 0
      ? query.severities.join(',')
      : undefined;
  const res = await request(
    `/v1/admin/notifications${buildQuery({
      search: query.search,
      status: query.status,
      severity,
      audience: query.audience,
      expiry: query.expiry,
      cursor: query.cursor,
    })}`,
  );
  if (!res.ok) throw await notifError(res);
  const page = await parseJson(res, userNotificationPageDto);
  return { items: page.items, nextCursor: page.nextCursor ?? null };
}

export async function createAdminNotification(body: CreateUserNotificationBody): Promise<void> {
  const res = await request('/v1/admin/notifications', { method: 'POST', body });
  if (!res.ok) throw await notifError(res);
}

export async function publishAdminNotification(id: string): Promise<void> {
  const res = await request(`/v1/admin/notifications/${id}/publish`, { method: 'POST' });
  if (!res.ok) throw await notifError(res);
}

export async function deleteAdminNotification(id: string): Promise<void> {
  const res = await request(`/v1/admin/notifications/${id}`, { method: 'DELETE' });
  if (!res.ok) throw await notifError(res);
}

export async function bulkDeleteDraftNotifications(
  ids: readonly string[],
): Promise<BulkDeleteDraftNotificationsResponse> {
  const res = await request('/v1/admin/notifications/bulk', {
    method: 'POST',
    body: { ids, action: 'deleteDrafts' },
  });
  if (!res.ok) throw await notifError(res);
  return parseJson(res, bulkDeleteDraftNotificationsResponseDto);
}
